import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Avatar,
  Slide,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import { AttachFile, Image } from "@mui/icons-material";
import {
  Bot,
  ChevronDown,
  ChevronUp,
  GripVertical,
  List,
  Maximize2,
  Minimize2,
  RefreshCw,
  Settings,
} from "lucide-react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { useDictation } from "../../hooks/useDictation";
import {
  PERSONAL_ASSISTANT_NAME,
  PersonalAssistantThread,
  PersonalAssistantMessage,
  PersonalAssistantPreferences,
  getImageUrlWithSize,
  DOCUMENT_ACCEPT_TYPES,
  isAllowedDocumentType,
} from "../../constants";
import { MessageBlock } from "../../types";
import { hostname } from "../../utils";
import PersonalAssistantSettings from "./PersonalAssistantSettings";
import PersonalAssistantThreadList from "./PersonalAssistantThreadList";
import { StructuredMessageContent } from "../StructuredChatMessage";
import ChatInput from "../ChatInput";

interface Message {
  id: string;
  role: "user" | "assistant";
  text: string;
  timestamp: number;
  blocks?: MessageBlock[];
  attachments?: Attachment[];
}

interface Attachment {
  id: string;
  previewUrl?: string;
  url?: string;
  filename?: string;
  type?: string;
  content?: string; // For documents - extracted text content
  tokens?: number;
}

interface PersonalAssistantProps {
  getToken: () => Promise<string>;
  companyId?: string;
  externalOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onPreferencesLoaded?: (prefs: { personaName?: string | null; personaImageUrl?: string | null } | null) => void;
}

const PersonalAssistant: React.FC<PersonalAssistantProps> = ({
  getToken,
  companyId,
  externalOpen,
  onOpenChange,
  onPreferencesLoaded,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTopRef = useRef(0);
  const [autoScroll, setAutoScroll] = useState(true);

  // Sync with external open state (for mobile menu control)
  useEffect(() => {
    if (externalOpen !== undefined && externalOpen !== isOpen) {
      setIsOpen(externalOpen);
    }
  }, [externalOpen]);

  // Notify parent when open state changes
  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  // Cancel functionality
  const abortControllerRef = useRef<AbortController | null>(null);
  // Track the current request to handle overlapping requests
  const currentRequestIdRef = useRef<string | null>(null);

  // Attachment state
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // Dictation hook - enabled by default
  const {
    isRecording: isDictating,
    isLoading: isDictationLoading,
    startDictation,
    stopDictation,
  } = useDictation({
    onTranscript: (text, isFinal) => {
      if (isFinal && text.trim()) {
        setInput((prev) => {
          const trimmedPrev = prev.trim();
          if (trimmedPrev) {
            return trimmedPrev + " " + text.trim();
          }
          return text.trim();
        });
      }
    },
  });

  // New state for threads and preferences
  const [threads, setThreads] = useState<PersonalAssistantThread[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<PersonalAssistantPreferences | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string>("free");
  const [showSettings, setShowSettings] = useState(false);
  const [showThreadList, setShowThreadList] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Helper to find the best thread to select when opening
  // Prefers non-heartbeat threads over heartbeat threads
  const findPreferredThread = (threadList: PersonalAssistantThread[]): PersonalAssistantThread | null => {
    if (!threadList || threadList.length === 0) return null;

    // Find the first non-heartbeat thread (threads are already sorted by pinned, then lastMessageAt)
    const nonHeartbeatThread = threadList.find(t => !t.title?.startsWith("🫀 Heartbeat"));

    // If there's a non-heartbeat thread, prefer it; otherwise fall back to the first thread
    return nonHeartbeatThread || threadList[0];
  };

  // Position as percentage from right (0 = right edge, 100 = left edge)
  const [rightPercent, setRightPercent] = useState<number>(() => {
    const saved = localStorage.getItem("personalAssistantRightPercent");
    return saved ? parseFloat(saved) : 0;
  });
  const [isDragging, setIsDragging] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const justDraggedRef = useRef(false);
  const barWidth = 280; // minWidth of the bar

  // Track window width so positions recompute on resize (prevents off-screen when
  // browser shrinks below the saved position's original viewport)
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Expanded window width for the current viewport — must match the sx values below
  // so we can clamp the right offset and keep it fully on-screen.
  const getExpandedWidth = () => {
    if (windowWidth < theme.breakpoints.values.sm) return windowWidth - 48; // xs
    if (windowWidth < theme.breakpoints.values.md) return 420; // sm
    return 480; // md+
  };

  // Save position to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("personalAssistantRightPercent", rightPercent.toString());
  }, [rightPercent]);

  // Handle drag start
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDragging(true);
  };

  // Handle drag move and end
  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const winW = window.innerWidth;

      // When dragging the expanded window, use its width so the cursor tracks the
      // window's horizontal center; otherwise use the collapsed bar width.
      const activeWidth = isOpen && !isFullscreen && !isMobile ? getExpandedWidth() : barWidth;

      // Calculate right position based on cursor position (center of the active element)
      const rightPx = winW - clientX - activeWidth / 2;

      // Convert to percentage and clamp between 0 and max
      const maxRightPx = winW - activeWidth - 40; // 20px margin on each side
      const percent = maxRightPx > 0
        ? Math.max(0, Math.min(100, (rightPx / maxRightPx) * 100))
        : 0;

      setRightPercent(percent);
    };

    const handleEnd = () => {
      setIsDragging(false);
      // Set flag to prevent click from opening the window
      justDraggedRef.current = true;
      setTimeout(() => {
        justDraggedRef.current = false;
      }, 100);
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd);
    document.addEventListener("touchmove", handleMove);
    document.addEventListener("touchend", handleEnd);

    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, isOpen, isFullscreen, isMobile, windowWidth]);

  // Calculate actual right position in pixels for a given element width.
  // Clamps to keep the element fully on-screen even if the viewport shrank since
  // the saved position was set, or if the expanded window is wider than the bar.
  const getRightPosition = (elementWidth: number = barWidth) => {
    const maxRightPx = windowWidth - elementWidth - 40;
    if (maxRightPx <= 0) return 20;
    const desired = 20 + (rightPercent / 100) * maxRightPx;
    return Math.max(20, Math.min(desired, windowWidth - elementWidth - 20));
  };

  // Load preferences on mount so avatar shows in collapsed bar
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const token = await getToken();
        const prefsRes = await axios.get(`${hostname}/personal-assistant/preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPreferences(prefsRes.data);
        // Notify parent of loaded preferences (for mobile menu display)
        if (onPreferencesLoaded) {
          onPreferencesLoaded({
            personaName: prefsRes.data?.personaName,
            personaImageUrl: prefsRes.data?.personaImageUrl,
          });
        }
      } catch (error) {
        console.error("Failed to load preferences:", error);
      }
    };

    if (!preferences) {
      loadPreferences();
    }
  }, []);

  // Load threads and other data when chat is opened
  // Always refresh when opening to pick up SMS messages that may have come in
  useEffect(() => {
    if (isOpen && !loadingData) {
      if (threads.length === 0) {
        // First load - get everything
        loadInitialData();
      } else {
        // Already have threads - refresh current thread and thread list
        refreshCurrentThread();
      }
    }
  }, [isOpen]);

  // Refresh current thread to pick up new messages (e.g., from SMS)
  const refreshCurrentThread = async () => {
    try {
      const token = await getToken();

      // Refresh thread list to show any new threads or updated previews
      const threadsRes = await axios.get(`${hostname}/personal-assistant/threads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setThreads(threadsRes.data.threads || []);

      // If we have a current thread, refresh its messages
      if (currentThreadId) {
        await loadThread(currentThreadId, token);
      } else if (threadsRes.data.threads?.length > 0) {
        // No current thread but threads exist - load preferred thread (non-heartbeat if available)
        const preferred = findPreferredThread(threadsRes.data.threads);
        if (preferred) {
          await loadThread(preferred.id, token);
        }
      }
    } catch (error) {
      console.error("Failed to refresh thread:", error);
    }
  };

  const loadInitialData = async () => {
    setLoadingData(true);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch threads, preferences, and usage (for plan) in parallel
      const [threadsRes, prefsRes, usageRes] = await Promise.all([
        axios.get(`${hostname}/personal-assistant/threads`, { headers }),
        axios.get(`${hostname}/personal-assistant/preferences`, { headers }),
        axios.get(`${hostname}/usage`, { headers }),
      ]);

      setThreads(threadsRes.data.threads || []);
      setPreferences(prefsRes.data);
      setCurrentPlan(usageRes.data?.usage?.subscriptionType || "free");

      // Load preferred thread if exists (non-heartbeat threads preferred)
      if (threadsRes.data.threads?.length > 0) {
        const preferred = findPreferredThread(threadsRes.data.threads);
        if (preferred) {
          await loadThread(preferred.id, token);
        }
      }
    } catch (error) {
      console.error("Failed to load initial data:", error);
    } finally {
      setLoadingData(false);
    }
  };

  const loadThread = async (threadId: string, token?: string) => {
    try {
      const authToken = token || (await getToken());
      const response = await axios.get(`${hostname}/personal-assistant/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const threadMessages: Message[] = (response.data.messages || []).map(
        (m: PersonalAssistantMessage) => ({
          id: m.id,
          role: m.role,
          text: m.text,
          timestamp: m.timestamp,
          blocks: m.blocks as MessageBlock[] | undefined,
          attachments: m.attachments as Attachment[] | undefined,
        })
      );

      setMessages(threadMessages);
      setCurrentThreadId(threadId);
    } catch (error) {
      console.error("Failed to load thread:", error);
    }
  };

  const loadThreads = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/personal-assistant/threads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setThreads(response.data.threads || []);
    } catch (error) {
      console.error("Failed to load threads:", error);
    }
  };

  // Scroll to bottom function with smooth behavior
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, []);

  // Scroll to bottom when messages change (throttled for smooth scrolling)
  useEffect(() => {
    if (autoScroll) {
      // Throttle scroll updates to make it smoother
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        scrollToBottom();
      }, 50); // Small delay to batch rapid updates
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [messages, autoScroll, scrollToBottom]);

  // Detect user scroll and toggle auto-scroll
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100; // 100px threshold

    // Detect if user scrolled up
    const scrolledUp = scrollTop < lastScrollTopRef.current - 5; // 5px threshold

    // Update last scroll position
    lastScrollTopRef.current = scrollTop;

    // If user scrolled up, disable auto-scroll
    if (scrolledUp && autoScroll) {
      setAutoScroll(false);
      return;
    }

    // Re-enable auto-scroll when user scrolls to bottom
    if (isAtBottom && !autoScroll) {
      setAutoScroll(true);
    }
  }, [autoScroll]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleNewThread = () => {
    setMessages([]);
    setCurrentThreadId(null);
  };

  // Cancel the current streaming request
  const handleCancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const sendMessage = useCallback(async () => {
    if (!input.trim()) return;

    // If currently loading, the backend will handle signaling the previous request
    // to complete early and will wait for it before processing the new message.
    // We don't abort here - just let the new request go through.

    // Enable auto-scroll when user sends a message
    setAutoScroll(true);

    // Capture current attachments before clearing
    const currentAttachments = [...attachments];

    const userMessage: Message = {
      id: uuidv4(),
      role: "user",
      text: input.trim(),
      timestamp: Date.now(),
      attachments: currentAttachments.length > 0 ? currentAttachments : undefined,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setAttachments([]); // Clear attachments after sending
    setIsLoading(true);

    // Create placeholder assistant message for streaming
    const assistantMessageId = uuidv4();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: "assistant",
      text: "",
      timestamp: Date.now(),
      blocks: [],
    };
    setMessages((prev) => [...prev, assistantMessage]);

    // Track this as the current request - this helps handle overlapping requests
    const requestId = assistantMessageId;
    currentRequestIdRef.current = requestId;

    // Create abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const token = await getToken();

      // Use fetch for streaming response
      // Send message text and attachments separately - backend will combine for AI
      const response = await fetch(`${hostname}/personal-assistant/chat`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: userMessage.text,
          threadId: currentThreadId,
          attachments: currentAttachments.map((a) => ({
            id: a.id,
            url: a.url,
            filename: a.filename,
            type: a.type,
            content: a.content,
          })),
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
      let messageBlocks: MessageBlock[] = [];
      let fullText = "";
      let jsonBuffer = "";
      const activeToolCalls = new Map<string, MessageBlock>();
      let receivedThreadId: string | null = null;
      let receivedMessageId: string | null = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        // Append new data to buffer
        jsonBuffer += value;

        // Process complete JSON objects (each on its own line)
        const lines = jsonBuffer.split("\n");
        const lastLine = lines.pop() || "";
        jsonBuffer = lastLine;

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith("{")) continue;

          let block: Record<string, unknown>;
          try {
            block = JSON.parse(trimmedLine);
          } catch {
            console.warn("Skipping malformed JSON:", trimmedLine.substring(0, 100));
            continue;
          }

          if (block.type === "metadata") {
            // Handle metadata block (threadId, messageId)
            receivedThreadId = block.threadId as string;
            receivedMessageId = block.messageId as string;
            if (receivedThreadId && receivedThreadId !== currentThreadId) {
              setCurrentThreadId(receivedThreadId);
              loadThreads();
            }
          } else if (block.type === "text") {
            // Handle text content
            const content = block.content as string;
            fullText += content;

            // Merge with last text block or create new one
            const lastBlock = messageBlocks[messageBlocks.length - 1];
            if (lastBlock && lastBlock.type === "text") {
              (lastBlock as { type: "text"; content: string }).content += content;
            } else {
              messageBlocks.push({ type: "text", content });
            }

            // Update message state
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, text: fullText, blocks: [...messageBlocks] }
                  : m
              )
            );
          } else if (block.type === "tool") {
            const toolBlock = block as unknown as MessageBlock;
            const toolId = block.id as string;

            // Update or add tool block
            if (activeToolCalls.has(toolId)) {
              // Update existing tool block
              const idx = messageBlocks.findIndex(
                (b) => b.type === "tool" && (b as { id?: string }).id === toolId
              );
              if (idx >= 0) {
                messageBlocks[idx] = toolBlock;
              }
            } else {
              // New tool block
              activeToolCalls.set(toolId, toolBlock);
              messageBlocks.push(toolBlock);
            }

            // Update message state
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, blocks: [...messageBlocks] }
                  : m
              )
            );
          } else if (block.type === "error") {
            // Handle error
            const errorContent = block.message as string;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMessageId
                  ? { ...m, text: errorContent, blocks: [] }
                  : m
              )
            );
          } else if (block.type === "done") {
            // Stream complete
          } else if (block.type === "earlyComplete") {
            // Stream completed early because a new message was sent
            // No need to mark as cancelled - just continue with normal completion
            console.log("Previous request completed early:", block.reason);
          }
        }
      }

      // Update final message ID if we received one from server
      if (receivedMessageId) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId
              ? { ...m, id: receivedMessageId as string }
              : m
          )
        );
      }
    } catch (error) {
      // Check if this was an abort/cancel
      if (error instanceof Error && error.name === "AbortError") {
        console.log("Request was cancelled by user");
        // Update the message to show it was cancelled
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId
              ? {
                  ...m,
                  text: m.text || "",
                  blocks: m.blocks?.length
                    ? [...m.blocks, { type: "text", content: "\n\n*[Response cancelled]*" }]
                    : [{ type: "text", content: "*[Response cancelled]*" }],
                }
              : m
          )
        );
      } else {
        console.error("Failed to send message:", error);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId
              ? {
                  ...m,
                  text: "Sorry, I encountered an error. Please try again in a moment.",
                  blocks: [],
                }
              : m
          )
        );
      }
    } finally {
      // Only clear loading state if this is still the current request
      // (a newer request may have started while this one was in progress)
      if (currentRequestIdRef.current === requestId) {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    }
  }, [input, currentThreadId, getToken, loadThreads, attachments]);

  const handleSavePreferences = async (updates: Partial<PersonalAssistantPreferences>) => {
    const token = await getToken();
    await axios.put(`${hostname}/personal-assistant/preferences`, updates, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // Reload preferences
    const prefsRes = await axios.get(`${hostname}/personal-assistant/preferences`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setPreferences(prefsRes.data);
  };

  const handleOpenSettings = async () => {
    setShowSettings(true);
    try {
      const token = await getToken();
      const prefsRes = await axios.get(`${hostname}/personal-assistant/preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreferences(prefsRes.data);
    } catch (err) {
      console.error("Failed to refresh preferences on settings open:", err);
    }
  };

  const handlePinThread = async (threadId: string, isPinned: boolean) => {
    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/personal-assistant/threads/${threadId}`,
        { isPinned },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadThreads();
    } catch (error) {
      console.error("Failed to pin thread:", error);
    }
  };

  const handleArchiveThread = async (threadId: string) => {
    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/personal-assistant/threads/${threadId}`,
        { isActive: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadThreads();

      // If archived thread is current, start a new one
      if (threadId === currentThreadId) {
        handleNewThread();
      }
    } catch (error) {
      console.error("Failed to archive thread:", error);
    }
  };

  const handleRenameThread = async (threadId: string, title: string) => {
    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/personal-assistant/threads/${threadId}`,
        { title },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update locally so the rename is visible without a full reload
      setThreads((prev) =>
        prev.map((t) => (t.id === threadId ? { ...t, title } : t))
      );
    } catch (error) {
      console.error("Failed to rename thread:", error);
    }
  };

  const handleDeleteThread = async (threadId: string) => {
    try {
      const token = await getToken();
      await axios.delete(`${hostname}/personal-assistant/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadThreads();

      // If deleted thread is current, start a new one
      if (threadId === currentThreadId) {
        handleNewThread();
      }
    } catch (error) {
      console.error("Failed to delete thread:", error);
    }
  };

  // Handle attachment upload
  const handleAttachmentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingFiles(true);
    try {
      const token = await getToken();

      for (const file of Array.from(files)) {
        // Skip images (handled separately) but validate document types
        if (!file.type.startsWith("image/") && !isAllowedDocumentType(file)) {
          console.warn("Skipping unsupported file type:", file.name);
          continue;
        }

        const formData = new FormData();
        formData.append("file", file);

        // Use personal assistant upload endpoint (no companyId required)
        const response = await axios.post(`${hostname}/personal-assistant/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });

        const uploadedFile = response.data;
        const isImage = file.type.startsWith("image/");

        setAttachments((prev) => [
          ...prev,
          {
            id: uploadedFile.id || uuidv4(),
            previewUrl: isImage ? (uploadedFile.publicUrl || uploadedFile.url) : undefined,
            url: isImage ? (uploadedFile.publicUrl || uploadedFile.url) : undefined,
            filename: uploadedFile.filename || file.name,
            type: uploadedFile.type || file.type,
            content: uploadedFile.content, // Document text content
            tokens: uploadedFile.tokens,
          },
        ]);
      }
    } catch (error) {
      console.error("Failed to upload attachment:", error);
    } finally {
      setUploadingFiles(false);
      // Clear the input so the same file can be uploaded again
      event.target.value = "";
    }
  };

  // Remove attachment
  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      {/* Collapsed Message Bar (LinkedIn-style) - hidden on mobile since it's controlled via drawer menu */}
      {!isOpen && !isMobile && (
        <Paper
          ref={barRef}
          elevation={3}
          onClick={() => {
            if (!isDragging && !justDraggedRef.current) {
              handleToggle();
            }
          }}
          sx={{
            position: "fixed",
            bottom: 0,
            right: getRightPosition(barWidth),
            minWidth: 280,
            height: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            px: 2,
            zIndex: theme.zIndex.drawer + 1,
            borderRadius: "8px 8px 0 0",
            cursor: isDragging ? "grabbing" : "pointer",
            backgroundColor: isDark ? "#0a0a0a" : "#f5f5f5",
            border: isDark ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.12)",
            borderBottom: "none",
            transition: isDragging ? "background-color 0.2s ease" : "background-color 0.2s ease, right 0.15s ease-out",
            "&:hover": {
              backgroundColor: isDark ? "#151515" : "#e8e8e8",
            },
            userSelect: "none",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Drag to reposition">
              <Box
                onMouseDown={handleDragStart}
                onTouchStart={handleDragStart}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: isDragging ? "grabbing" : "grab",
                  color: theme.palette.text.secondary,
                  p: 0.5,
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: isDark ? "#1a1a1a" : "#d8d8d8",
                  },
                }}
              >
                <GripVertical size={18} />
              </Box>
            </Tooltip>
            <Avatar
              src={getImageUrlWithSize(preferences?.personaImageUrl, 64)}
              sx={{
                width: 32,
                height: 32,
                backgroundColor: isDark ? "#1a1a1a" : "#e0e0e0",
                color: theme.palette.text.primary,
              }}
            >
              {!preferences?.personaImageUrl && <Bot size={18} />}
            </Avatar>
            <Typography variant="body2" fontWeight={600} sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {preferences?.personaName || PERSONAL_ASSISTANT_NAME}
            </Typography>
            <Chip label="Beta" size="small" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
          </Box>
          <ChevronUp size={20} color={theme.palette.text.secondary} />
        </Paper>
      )}

      {/* Expanded Chat Window */}
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 0,
            right: isFullscreen || isMobile ? 0 : getRightPosition(getExpandedWidth()),
            left: isFullscreen || isMobile ? 0 : "auto",
            top: isFullscreen || isMobile ? 0 : "auto",
            width: isFullscreen || isMobile
              ? "100vw"
              : { xs: "calc(100vw - 48px)", sm: 420, md: 480 },
            maxWidth: isFullscreen || isMobile ? "100vw" : 480,
            height: isFullscreen || isMobile ? "100vh" : { xs: "75vh", sm: 600 },
            maxHeight: isFullscreen || isMobile ? "100vh" : "80vh",
            display: "flex",
            flexDirection: "column",
            zIndex: isFullscreen || isMobile ? theme.zIndex.drawer + 3 : theme.zIndex.drawer + 1,
            borderRadius: isFullscreen || isMobile ? 0 : "8px 8px 0 0",
            overflow: "hidden",
            transition: "all 0.3s ease-in-out",
            border: isDark && !isFullscreen && !isMobile ? "1px solid rgba(255, 255, 255, 0.12)" : "none",
            borderBottom: "none",
          }}
        >
          {showThreadList ? (
            <Box
              sx={{
                pt: isMobile ? "env(safe-area-inset-top)" : 0,
                pb: isMobile ? "env(safe-area-inset-bottom)" : 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <PersonalAssistantThreadList
                onBack={() => setShowThreadList(false)}
                threads={threads}
                currentThreadId={currentThreadId}
                onSelectThread={(threadId) => loadThread(threadId)}
                onNewThread={handleNewThread}
                onRefresh={loadThreads}
                onPinThread={handlePinThread}
                onArchiveThread={handleArchiveThread}
                onDeleteThread={handleDeleteThread}
                onRenameThread={handleRenameThread}
                loading={loadingData}
              />
            </Box>
          ) : (
            <>
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  pt: isMobile ? "calc(12px + env(safe-area-inset-top))" : 1.5,
                  backgroundColor: isDark ? "#0a0a0a" : "#f5f5f5",
                  borderBottom: 1,
                  borderColor: "divider",
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {!isFullscreen && !isMobile && (
                    <Tooltip title="Drag to reposition">
                      <Box
                        onMouseDown={handleDragStart}
                        onTouchStart={handleDragStart}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: isDragging ? "grabbing" : "grab",
                          color: theme.palette.text.secondary,
                          p: 0.5,
                          borderRadius: 1,
                          "&:hover": {
                            backgroundColor: isDark ? "#1a1a1a" : "#d8d8d8",
                          },
                          touchAction: "none",
                        }}
                      >
                        <GripVertical size={18} />
                      </Box>
                    </Tooltip>
                  )}
                  <Tooltip title="Conversations">
                    <IconButton size="small" onClick={() => setShowThreadList(true)}>
                      <List size={18} />
                    </IconButton>
                  </Tooltip>
                  <Avatar
                    src={getImageUrlWithSize(preferences?.personaImageUrl, 80)}
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: isDark ? "#1a1a1a" : "#e0e0e0",
                      color: theme.palette.text.primary,
                    }}
                  >
                    {!preferences?.personaImageUrl && <Bot size={22} />}
                  </Avatar>
                  <Typography variant="h6" fontWeight={600}>
                    {preferences?.personaName || PERSONAL_ASSISTANT_NAME}
                  </Typography>
                  <Chip label="Beta" size="small" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
                </Box>
                <Box>
                  <Tooltip title="Settings">
                    <IconButton
                      size="small"
                      onClick={handleOpenSettings}
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      <Settings size={18} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="New conversation">
                    <IconButton
                      size="small"
                      onClick={handleNewThread}
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      <RefreshCw size={18} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
                    <IconButton
                      size="small"
                      onClick={() => setIsFullscreen(!isFullscreen)}
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {isFullscreen ? (
                        <Minimize2 size={18} />
                      ) : (
                        <Maximize2 size={18} />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Minimize">
                    <IconButton
                      size="small"
                      onClick={handleClose}
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      <ChevronDown size={18} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Messages Area */}
              <Box
                ref={messagesContainerRef}
                onScroll={handleScroll}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  minWidth: 0,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  backgroundColor: theme.palette.background.default,
                  overscrollBehavior: "contain",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                {messages.length === 0 && !loadingData && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      textAlign: "center",
                      color: theme.palette.text.secondary,
                      gap: 1,
                    }}
                  >
                    {preferences?.personaImageUrl ? (
                      <Avatar
                        src={getImageUrlWithSize(preferences.personaImageUrl, 128)}
                        sx={{ width: 64, height: 64, opacity: 0.7 }}
                      />
                    ) : (
                      <Bot size={64} style={{ opacity: 0.5 }} />
                    )}
                    <Typography variant="body2">
                      Hi! I'm {preferences?.personaName || "your executive assistant"}.
                      <br />
                      How can I help you today?
                    </Typography>
                  </Box>
                )}

                {loadingData && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}

                {messages.map((message) => (
                  <Box
                    key={message.id}
                    sx={{
                      display: "flex",
                      width: "100%",
                      minWidth: 0,
                      justifyContent: message.role === "user" ? "flex-end" : "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: "85%",
                        minWidth: 0,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        "& *": { maxWidth: "100%", minWidth: 0 },
                        "& img, & table": { maxWidth: "100%" },
                        color: theme.palette.text.primary,
                        ...(message.role === "user"
                          ? {
                              p: 1.5,
                              borderRadius: 2,
                              backgroundColor: isDark ? "#1a1a1a" : "#e8e8e8",
                            }
                          : {}),
                        "& p": {
                          margin: 0,
                        },
                        "& p:not(:last-child)": {
                          marginBottom: 1,
                        },
                        "& code": {
                          backgroundColor: theme.palette.action.selected,
                          padding: "2px 6px",
                          borderRadius: 1,
                          fontSize: "0.85em",
                        },
                        "& pre": {
                          backgroundColor: theme.palette.action.selected,
                          padding: 1,
                          borderRadius: 1,
                          overflow: "auto",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          overflowX: "auto",
                          "& code": {
                            backgroundColor: "transparent",
                            padding: 0,
                          },
                        },
                        "& ul, & ol": {
                          margin: 0,
                          paddingLeft: 2,
                        },
                        "& a": {
                          color: theme.palette.text.primary,
                          textDecoration: "underline",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        },
                      }}
                    >
                      {message.role === "assistant" && message.blocks && message.blocks.length > 0 ? (
                        <>
                          <StructuredMessageContent
                            blocks={message.blocks}
                            loading={false}
                            messageId={message.id}
                          />
                          {/* Moving dot indicator below content while streaming */}
                          {isLoading && messages[messages.length - 1]?.id === message.id && (
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                backgroundColor: theme.palette.text.primary,
                                mt: 1.5,
                                animation: "pulse 1.2s ease-in-out infinite",
                                "@keyframes pulse": {
                                  "0%, 100%": {
                                    transform: "scale(0.6)",
                                  },
                                  "50%": {
                                    transform: "scale(1)",
                                  },
                                },
                              }}
                            />
                          )}
                        </>
                      ) : message.role === "user" ? (
                        <>
                          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {message.text}
                          </Typography>
                          {/* Show attachment chips */}
                          {message.attachments && message.attachments.length > 0 && (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                              {message.attachments.map((attachment, idx) => {
                                const isImage = attachment.previewUrl || attachment.type?.startsWith("image/");
                                const attachmentImgUrl = attachment.url || attachment.previewUrl || "";
                                const tooltipContent = isImage
                                  ? attachmentImgUrl
                                    ? (
                                      <img
                                        src={/^https?:\/\/[a-zA-Z0-9]/.test(attachmentImgUrl) ? attachmentImgUrl : ""}
                                        alt={attachment.filename || "Image"}
                                        style={{ maxWidth: 200, maxHeight: 200 }}
                                      />
                                    )
                                    : attachment.filename || "Image"
                                  : attachment.content
                                    ? attachment.content.substring(0, 500) + (attachment.content.length > 500 ? "..." : "")
                                    : attachment.filename || "Document";
                                return (
                                  <Tooltip
                                    key={attachment.id || idx}
                                    title={tooltipContent}
                                    arrow
                                    placement="top"
                                  >
                                    <Chip
                                      icon={isImage ? <Image sx={{ fontSize: 16 }} /> : <AttachFile sx={{ fontSize: 16 }} />}
                                      label={attachment.filename || (isImage ? "Image" : "Document")}
                                      size="small"
                                      variant="outlined"
                                      sx={{ maxWidth: 200 }}
                                    />
                                  </Tooltip>
                                );
                              })}
                            </Box>
                          )}
                        </>
                      ) : message.role === "assistant" ? (
                        <>
                          {message.text ? (
                            <StructuredMessageContent
                              blocks={[{ type: "text", content: message.text }]}
                              loading={false}
                              messageId={message.id}
                            />
                          ) : null}
                          {/* Thinking text and moving dot indicator while streaming */}
                          {isLoading && messages[messages.length - 1]?.id === message.id && (
                            <>
                              {!message.text && (
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                  Thinking...
                                </Typography>
                              )}
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  backgroundColor: theme.palette.text.primary,
                                  mt: message.text ? 1.5 : 0,
                                  animation: "pulse 1.2s ease-in-out infinite",
                                  "@keyframes pulse": {
                                    "0%, 100%": {
                                      transform: "scale(0.6)",
                                    },
                                    "50%": {
                                      transform: "scale(1)",
                                    },
                                  },
                                }}
                              />
                            </>
                          )}
                        </>
                      ) : (
                        <StructuredMessageContent
                          blocks={[{ type: "text", content: message.text }]}
                          loading={false}
                          messageId={message.id}
                        />
                      )}
                    </Box>
                  </Box>
                ))}


                <div ref={messagesEndRef} />
              </Box>

              {/* Input Area */}
              <Box
                sx={{
                  p: 1.5,
                  pb: isMobile ? "calc(12px + env(safe-area-inset-bottom))" : 1.5,
                  borderTop: 1,
                  borderColor: "divider",
                  backgroundColor: isDark ? "#0a0a0a" : theme.palette.background.paper,
                  flexShrink: 0,
                }}
              >
                <ChatInput
                  value={input}
                  onChange={setInput}
                  onSend={sendMessage}
                  onStop={handleCancelRequest}
                  placeholder="Ask me anything..."
                  disabled={false}
                  sending={isLoading}
                  rows={2}
                  enablePopOut={true}
                  popOutDialogTitle="Compose Message"
                  popOutDialogDescription="Write your message to your executive assistant."
                  showAttachments={true}
                  attachments={attachments}
                  onAttachmentUpload={handleAttachmentUpload}
                  onRemoveAttachment={handleRemoveAttachment}
                  uploadingFiles={uploadingFiles}
                  acceptedFileTypes={`image/*,${DOCUMENT_ACCEPT_TYPES}`}
                  inputId="personal-assistant-file-upload"
                  showDictation={true}
                  isDictating={isDictating}
                  isDictationLoading={isDictationLoading}
                  onStartDictation={startDictation}
                  onStopDictation={stopDictation}
                />
              </Box>
            </>
          )}
        </Paper>
      </Slide>

      {/* Settings Dialog */}
      <PersonalAssistantSettings
        open={showSettings}
        onClose={() => setShowSettings(false)}
        preferences={preferences}
        currentPlan={currentPlan}
        onSave={handleSavePreferences}
        loading={loadingData}
        getToken={getToken}
        companyId={companyId}
      />

    </>
  );
};

export default PersonalAssistant;
